import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import {
  FEATURE_FLAGS,
  FeatureFlag,
  FeatureFlags,
} from '../feature-flags/featureFlags';
import {
  FeatureFlagOverrides,
  FeatureFlagsService,
} from '../services/feature-flags.service';

interface FeatureFlagsState {
  flags: FeatureFlags;
  overrides: FeatureFlagOverrides;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}

const initialState: FeatureFlagsState = {
  flags: FEATURE_FLAGS,
  overrides: {},
  isLoading: false,
  isLoaded: false,
  error: null,
};

export const FeatureFlagsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(FeatureFlagsService)) => ({
    load(): Observable<FeatureFlags> {
      patchState(store, { isLoading: true, error: null });
      return service.getFeatureFlags().pipe(
        tap(() => {
          const snapshot = service.getSnapshot();
          patchState(store, {
            flags: snapshot.flags,
            overrides: snapshot.overrides,
            isLoaded: true,
          });
        }),
        catchError((error: unknown) => {
          const message =
            error instanceof Error ? error.message : 'Failed to load feature flags';
          patchState(store, { error: message });
          return of(store.flags());
        }),
        finalize(() => patchState(store, { isLoading: false })),
      );
    },
    isEnabled(flag: FeatureFlag): boolean {
      return store.flags()[flag] === true;
    },
    setFlag(flag: FeatureFlag, enabled: boolean): void {
      const snapshot = service.setOverride(flag, enabled);
      patchState(store, {
        flags: snapshot.flags,
        overrides: snapshot.overrides,
      });
    },
    resetOverrides(): void {
      const snapshot = service.clearOverrides();
      patchState(store, {
        flags: snapshot.flags,
        overrides: snapshot.overrides,
      });
    },
  })),
);
